import type { ColumnDef } from '@tanstack/react-table';
import { AreaRouteCell } from '@/components/common/AreaRouteCell';
import { createSerialColumn, StackedCellText } from '@/components/common/DataTable';
import { WorkTypeBadge } from '@/components/common/WorkTypeBadge';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { FiscalYearBadge } from '@/features/billing/components/FiscalYearBadge';
import { PersonNameCell } from '@/features/billing/components/PersonNameCell';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { formatNumber } from '@/utils/number';
import { formatDisplayDate } from '@/utils/date';
import type { DcrApproveRow } from '../types/dcrApprove.types';
import { DcrApproveStatusCell } from './DcrApproveStatusCell';

const dash = (value: string | null | undefined) =>
  value === null || value === undefined || value.trim() === '' ? EMPTY_VALUE : value;

const time = (value: string | null) => (value === null ? EMPTY_VALUE : value.slice(11, 16));

export function getDcrApproveColumns(
  searchText: string | undefined,
): ColumnDef<DcrApproveRow, unknown>[] {
  return [
    createSerialColumn<DcrApproveRow>(),
    {
      accessorKey: 'zmName',
      header: 'ZM Name',
      cell: ({ row }) => (
        <PersonNameCell
          name={dash(row.original.zmName)}
          designationName={row.original.zmDesignation}
          mobile={row.original.zmMobile}
          note={
            row.original.isRefZm
              ? `ref ${dash(row.original.refZmName)}${OPTION_DETAIL_SEPARATOR}${dash(row.original.refZmMobile)}`
              : undefined
          }
          searchText={searchText}
        />
      ),
    },
    {
      accessorKey: 'dcrNo',
      header: 'DCR No',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.dcrNo}
          primaryClassName="font-medium tabular-nums"
          secondary={
            row.original.fiscalYear ? (
              <FiscalYearBadge fiscalYear={row.original.fiscalYear} />
            ) : null
          }
        />
      ),
    },
    {
      accessorKey: 'dcrType',
      header: 'Work Type',
      cell: ({ row }) => (
        <WorkTypeBadge code={row.original.dcrTypeCode} name={row.original.dcrType} />
      ),
    },
    {
      accessorKey: 'dcrDate',
      header: 'DCR Date / Time',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.dcrDate)}
          secondary={
            <span className="tabular-nums">
              {time(row.original.timeFrom)} – {time(row.original.timeTo)}
            </span>
          }
        />
      ),
    },
    {
      id: 'institute',
      header: 'Institution / Type · EIIN',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.instituteName)}
          secondary={[
            row.original.instituteTypeName,
            row.original.eiinNumber && `EIIN ${row.original.eiinNumber}`,
          ]
            .filter(Boolean)
            .join(OPTION_DETAIL_SEPARATOR)}
        />
      ),
    },
    {
      id: 'route',
      header: 'Area From – To',
      cell: ({ row }) => (
        <AreaRouteCell
          fromName={row.original.workAreaFromName}
          toName={row.original.workAreaToName}
          comments={row.original.comments}
          userId={row.original.userId}
          personName={row.original.zmName}
          dcrDate={row.original.dcrDate}
        />
      ),
    },
    {
      id: 'transport',
      header: 'Transport (claimed → approved)',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.transportType)}
          secondary={row.original.approveTransportType}
        />
      ),
    },
    {
      accessorKey: 'fareAmount',
      header: 'Fare (Tk)',
      cell: ({ row }) => (
        <div className="min-w-24 text-right">
          <p className="tabular-nums">{formatNumber(row.original.fareAmount)}</p>
          <p className="text-xs text-muted-foreground tabular-nums">
            approved {formatNumber(row.original.approveFareAmount)}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'approveType',
      header: () => <span className="block text-center">Status</span>,
      cell: ({ row }) => (
        <DcrApproveStatusCell
          approveTypeNo={row.original.approveTypeNo}
          approveType={row.original.approveType}
          isVerify={row.original.isVerify}
        />
      ),
    },
  ];
}
